/**
 * Weight unit primitives.
 *
 * All weights are stored throughout the application as **integer grams**.
 * Rationale (requirement A6): the user must be able to enter arbitrary valid
 * weights (0.25 / 0.5 / 1.25 / 2.5 kg increments and anything else), and
 * floating-point kilograms accumulate representation error under summation and
 * fail exact equality comparisons. Integers do neither.
 *
 * This module is a Phase 1 foundation primitive, not domain logic.
 */

/** Smallest weight the application will represent: 1 gram. */
export const GRAMS_PER_KG = 1000;

/** Thrown when a caller supplies a weight that cannot be represented. */
export class InvalidWeightError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidWeightError';
  }
}

/**
 * Convert kilograms to integer grams.
 *
 * @throws {InvalidWeightError} if the value is not finite, is negative, or has
 * more precision than 1 gram.
 */
export function kgToGrams(kg: number): number {
  if (!Number.isFinite(kg)) {
    throw new InvalidWeightError(`Weight must be a finite number, received: ${String(kg)}`);
  }
  if (kg < 0) {
    throw new InvalidWeightError(`Weight must not be negative, received: ${kg}`);
  }
  // Round rather than truncate: 80.5 * 1000 is 80499.99999999999 in IEEE-754.
  const grams = Math.round(kg * GRAMS_PER_KG);
  if (Math.abs(kg * GRAMS_PER_KG - grams) > 0.001) {
    throw new InvalidWeightError(`Weight has sub-gram precision and cannot be stored: ${kg}`);
  }
  return grams;
}

/** Convert integer grams back to kilograms. */
export function gramsToKg(grams: number): number {
  if (!Number.isInteger(grams)) {
    throw new InvalidWeightError(`Grams must be an integer, received: ${grams}`);
  }
  return grams / GRAMS_PER_KG;
}

/**
 * Format grams for display, trimming trailing zeros.
 * 80000 -> "80", 80500 -> "80.5", 81250 -> "81.25"
 */
export function formatKg(grams: number): string {
  const kg = gramsToKg(grams);
  return kg
    .toFixed(3)
    .replace(/0+$/, '')
    .replace(/\.$/, '');
}

/**
 * Round a weight in grams to the nearest multiple of `incrementG`.
 * Used later by the progression engine, which must land on weights that can
 * actually be loaded onto a bar.
 */
export function roundToIncrement(grams: number, incrementG: number): number {
  if (!Number.isInteger(incrementG) || incrementG <= 0) {
    throw new InvalidWeightError(`Increment must be a positive integer, received: ${incrementG}`);
  }
  return Math.round(grams / incrementG) * incrementG;
}
